/**
 * Excerpt operations shared by the icon menu, the Excerpts panel and the
 * Tagged Items list (#277). Each refreshes the in-tab store afterwards so
 * marks and embeds update without a reload.
 */
import type { ExcerptStore } from '../../contexts/ExcerptSyncContext';
import { addDestination, deleteExcerpt, removeDestination, isSourceType, type ExcerptView, type ItemRef, type SourceType } from '../../lib/excerpts';
import type { PickedItem } from './DestinationPicker';

/** Client route for an item. Meetings open by id through the standalone route, which loads any instance. */
export function itemUrl(type: string, id: string): string {
  switch (type) {
    case 'task': return `/tasks/${id}`;
    case 'note': return `/notes/${id}`;
    case 'meeting_instance': return `/meetings/standalone/${id}`;
    case 'meeting_series': return `/meetings/${id}`;
    case 'scratchpad': return `/scratchpad/${id}`;
    case 'scope': return `/projects/${id}`;
    case 'reference_doc': return `/views/${id}`;
    case 'person': return `/people/${id}`;
    case 'tag': return '/tags';
    default: return '/';
  }
}

/** The source page, scrolled to the excerpt (DossierEditor reads the hash). */
export const sourceUrl = (v: ExcerptView) => `${itemUrl(v.source.type, v.source.id)}#excerpt-${v.id}`;

function refresh(store: ExcerptStore | null, v: ExcerptView) {
  if (!store) return;
  store.invalidate([v.id]);
  if (isSourceType(v.source.type)) void store.refreshSource({ type: v.source.type as SourceType, id: v.source.id });
}

/**
 * Applies a new destination set. Bodies open in this tab get their embed from
 * their own editor (`placed`), so their autosave and this request never clash.
 */
export async function changeDestinations(store: ExcerptStore | null, v: ExcerptView, picked: PickedItem[]): Promise<void> {
  const key = (d: { type: string; id: string }) => `${d.type}:${d.id}`;
  const before = new Set(v.destinations.map(key));
  const after = new Set(picked.map(key));
  for (const p of picked) {
    if (before.has(key(p))) continue;
    const editor = store?.editorFor(p);
    const placed = !!editor && editor.commands.insertExcerptEmbed(v.id);
    await addDestination(v.id, { type: p.type, id: p.id }, placed);
  }
  for (const d of v.destinations) {
    if (!after.has(key(d))) await removeDestination(v.id, { type: d.type as ItemRef['type'], id: d.id });
  }
  refresh(store, v);
}

export async function removeFrom(store: ExcerptStore | null, v: ExcerptView, dest: { type: string; id: string }): Promise<{ excerptNowEmpty: boolean }> {
  const out = await removeDestination(v.id, { type: dest.type as ItemRef['type'], id: dest.id });
  refresh(store, v);
  return out;
}

export async function removeExcerpt(store: ExcerptStore | null, v: ExcerptView, deleteLinked: boolean): Promise<void> {
  await deleteExcerpt(v.id, deleteLinked);
  refresh(store, v);
}
